import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Clock,
  User,
  FileText,
  Plus,
  Edit,
  Trash2,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  CheckCircle,
  XCircle,
  ArrowRight,
  Download
} from 'lucide-react';
import { useAuditHistory, useAuditSnapshot } from '../../hooks/useAuditHistory';
import { capAuditText, labelForId } from '../../utils/auditDisplay';
import DiffViewer, { StatusChangeDiff } from './DiffViewer';
import { formatDateTime } from '../../utils/dateUtils';

/**
 * Get icon and color for an action type.
 */
const getActionStyle = (action) => {
  switch (action) {
    case 'create':
      return {
        icon: <Plus className="w-5 h-5" />,
        color: 'bg-green-500',
        label: 'Created'
      };
    case 'update':
      return {
        icon: <Edit className="w-5 h-5" />,
        color: 'bg-blue-500',
        label: 'Updated'
      };
    case 'delete':
      return {
        icon: <Trash2 className="w-5 h-5" />,
        color: 'bg-red-500',
        label: 'Deleted'
      };
    case 'status_change':
      return {
        icon: <ArrowRight className="w-5 h-5" />,
        color: 'bg-orange-500',
        label: 'Status Changed'
      };
    case 'approve':
      return {
        icon: <CheckCircle className="w-5 h-5" />,
        color: 'bg-green-500',
        label: 'Approved'
      };
    case 'reject':
      return {
        icon: <XCircle className="w-5 h-5" />,
        color: 'bg-red-500',
        label: 'Rejected'
      };
    case 'export':
      return {
        icon: <Download className="w-5 h-5" />,
        color: 'bg-purple-500',
        label: 'Exported PDF'
      };
    case 'attach_files':
      return {
        icon: <Plus className="w-5 h-5" />,
        color: 'bg-teal-500',
        label: 'Files Attached'
      };
    case 'detach_file':
      return {
        icon: <Trash2 className="w-5 h-5" />,
        color: 'bg-orange-500',
        label: 'File Detached'
      };
    case 'action_item_added':
      return {
        icon: <Plus className="w-5 h-5" />,
        color: 'bg-green-600',
        label: 'Action Item Added'
      };
    case 'action_item_status_changed':
      return {
        icon: <ArrowRight className="w-5 h-5" />,
        color: 'bg-orange-500',
        label: 'Action Item Status Changed'
      };
    case 'action_item_updated':
      return {
        icon: <Edit className="w-5 h-5" />,
        color: 'bg-blue-500',
        label: 'Action Item Updated'
      };
    case 'attendance_marked':
      return {
        icon: <CheckCircle className="w-5 h-5" />,
        color: 'bg-teal-500',
        label: 'Attendance Marked'
      };
    case 'version_uploaded':
      return {
        icon: <Plus className="w-5 h-5" />,
        color: 'bg-indigo-500',
        label: 'New Version Uploaded'
      };
    case 'content_updated':
      return {
        icon: <Edit className="w-5 h-5" />,
        color: 'bg-indigo-500',
        label: 'Content Updated'
      };
    default:
      return {
        icon: <FileText className="w-5 h-5" />,
        color: 'bg-gray-500',
        label: action
          .replace(/_/g, ' ')
          .replace(/\b\w/g, c => c.toUpperCase())
      };
  }
};

/**
 * Format timestamp for display — delegates to centralized dateUtils.
 */
const formatTimestamp = (timestamp) => {
  if (!timestamp) return 'Unknown';
  const result = formatDateTime(timestamp);
  return result === '\u2014' ? 'Unknown' : result;
};

/**
 * Single timeline entry component.
 */
/**
 * The field diff for one entry, in DiffViewer's shape.
 *
 * WHY THIS IS NOT JUST `entry.details.changes`
 *
 * The backend records a diff in one of two shapes depending on which format
 * the calling route used:
 *
 *   - field-diff format lands in `details.changes` as {field: {old_value, new_value}}
 *   - the older {old, new, fields} format lands in `details.old_values` /
 *     `details.new_values` / `details.changed_fields`
 *
 * This panel only ever read the first, so a module using the second showed
 * "No changes recorded" against an entry that recorded them perfectly well —
 * CAPA (0 of 8 entries), training records (0 of 13) and documents (0 of 8)
 * never displayed a diff at all, while incidents showed 2 of 22.
 *
 * Reading both makes every module's record history show the same thing, and
 * matches what Admin -> Audit History derives from the same rows.
 */
const deriveChanges = (entry) => {
  const details = entry?.details;
  if (!details || typeof details !== 'object') return null;

  if (details.changes && Object.keys(details.changes).length > 0) {
    return details.changes;
  }

  const oldValues = details.old_values && typeof details.old_values === 'object'
    ? details.old_values : {};
  const newValues = details.new_values && typeof details.new_values === 'object'
    ? details.new_values : {};
  const fields = Array.isArray(details.changed_fields) && details.changed_fields.length
    ? details.changed_fields
    : Object.keys({ ...oldValues, ...newValues });

  if (!fields.length) return null;

  const built = {};
  for (const field of fields) {
    built[field] = {
      old_value: field in oldValues ? oldValues[field] : undefined,
      new_value: field in newValues ? newValues[field] : undefined,
    };
  }
  return built;
};

const TimelineEntry = ({ entry, entityType, entityId, onViewSnapshot, referenceNames }) => {
  const [expanded, setExpanded] = useState(false);
  const actionStyle = getActionStyle(entry.action);

  const entryChanges = deriveChanges(entry);
  const hasChanges = Boolean(entryChanges && Object.keys(entryChanges).length > 0);
  const hasStatusChange = entry.details?.status_change;
  const hasSnapshot = entry.details?.snapshot;
  const isExport = entry.action === 'export';
  const isApproval = entry.action === 'approve' || entry.action === 'reject';
  const isFileAction = entry.action === 'attach_files' || entry.action === 'detach_file';
  const isActionItem = entry.action?.startsWith('action_item_');
  const changeCount = hasChanges ? Object.keys(entryChanges).length : 0;

  // Build approval-specific label
  let displayLabel = actionStyle.label;
  let approvalDescription = '';
  if (isApproval) {
    const details = entry.details || {};
    const approverName = details.approver_name || entry.actor?.name || entry.actor?.email || 'Unknown';
    const approverRole = details.approver_role || entry.actor?.role || '';
    const levelLabel = details.level_label || '';
    displayLabel = `${entry.action === 'approve' ? 'Approved' : 'Rejected'} by ${approverName} (${approverRole})`;
    approvalDescription = levelLabel + (details.comments ? ` — ${details.comments}` : '');
  }

  // Build action item context description
  const actionItemDescription = isActionItem ? (entry.details?.action_item_description || '') : '';
  const actionItemAssignee = isActionItem ? (entry.details?.action_item_assignee || '') : '';

  return (
    <div className="relative flex gap-4 items-start">
      {/* Icon */}
      <div className={`relative z-10 flex items-center justify-center w-12 h-12 rounded-full ${actionStyle.color} text-white shadow-lg flex-shrink-0`}>
        {actionStyle.icon}
      </div>

      {/* Content */}
      <div className="flex-1 pb-6">
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          {/* Header */}
          <div
            className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => setExpanded(!expanded)}
          >
            <div className="flex items-start justify-between">
              <div>
                <h4 className="text-sm font-bold text-gray-900">
                  {displayLabel}
                </h4>
                {isApproval && approvalDescription && (
                  <p className="text-sm text-gray-600 mt-1">
                    {approvalDescription}
                  </p>
                )}
                {isActionItem && actionItemDescription && (
                  <p className="text-sm text-gray-700 mt-1">
                    <span className="font-medium">Action Item:</span> {actionItemDescription}
                    {actionItemAssignee && <span className="text-gray-500"> (Assigned to: {actionItemAssignee})</span>}
                  </p>
                )}
                {hasStatusChange && !isApproval && (
                  <p className="text-sm text-gray-600 mt-1">
                    {entry.details.status_change.old_status || entry.details.status_change.old || 'Unknown'} → {entry.details.status_change.new_status || entry.details.status_change.new || 'Unknown'}
                  </p>
                )}
                {isExport && entry.details?.export_type && (
                  <p className="text-sm text-gray-600 mt-1">
                    {entry.details.export_type === 'incident_report_pdf' ? 'Incident Report PDF' :
                     entry.details.export_type === 'fishbone_analysis_pdf' ? 'Fishbone Analysis PDF' :
                     entry.details.export_type === 'india_form18_pdf' ? 'India Form 18 PDF' :
                     entry.details.export_type === 'singapore_mom_ireport_pdf' ? 'Singapore MOM iReport PDF' :
                     entry.details.export_type}
                  </p>
                )}
                {entry.action === 'attach_files' && entry.details?.filenames && (
                  <p className="text-sm text-gray-600 mt-1">
                    {entry.details.filenames.join(', ')}
                  </p>
                )}
                {entry.action === 'detach_file' && entry.details?.filename && (
                  <p className="text-sm text-gray-600 mt-1">
                    {entry.details.filename}
                  </p>
                )}
                {entry.action === 'version_uploaded' && entry.details && (
                  <p className="text-sm text-gray-600 mt-1">
                    {entry.details.version_number ? `${entry.details.version_number} • ` : ''}
                    {entry.details.file_name || ''}
                    {entry.details.change_description ? ` — ${entry.details.change_description}` : ''}
                  </p>
                )}
                {entry.action === 'content_updated' && entry.details && (
                  <p className="text-sm text-gray-600 mt-1">
                    {entry.details.version_number ? `${entry.details.version_number}` : ''}
                    {entry.details.change_description ? ` — ${entry.details.change_description}` : ''}
                  </p>
                )}
                {hasChanges && !hasStatusChange && !isApproval && !isFileAction && (
                  <p className="text-sm text-gray-600 mt-1">
                    {changeCount} field{changeCount !== 1 ? 's' : ''} changed
                  </p>
                )}
              </div>

              <div className="flex items-center gap-2">
                {(hasChanges || hasStatusChange || hasSnapshot) && (
                  <button className="text-gray-400 hover:text-gray-600">
                    {expanded ? (
                      <ChevronUp className="w-5 h-5" />
                    ) : (
                      <ChevronDown className="w-5 h-5" />
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* Actor and timestamp */}
            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
              <div className="flex items-center gap-1">
                <User className="w-3 h-3" />
                <span className="font-medium">{entry.actor?.email || 'Unknown'}</span>
                {entry.actor?.role && (
                  <span className="text-gray-400">({entry.actor.role})</span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                <span>{formatTimestamp(entry.timestamp)}</span>
              </div>
            </div>
          </div>

          {/* Expanded Content */}
          {expanded && (
            <div className="border-t border-gray-100 p-4 bg-gray-50">
              {/* Status Change */}
              {hasStatusChange && (
                <div className="mb-4">
                  <StatusChangeDiff
                    oldStatus={entry.details.status_change.old_status || entry.details.status_change.old}
                    newStatus={entry.details.status_change.new_status || entry.details.status_change.new}
                    reason={entry.details.status_change.reason}
                  />
                </div>
              )}

              {/* Field Changes */}
              {hasChanges && (
                <div className="mb-4">
                  <h5 className="text-sm font-medium text-gray-700 mb-2">Changes</h5>
                  <DiffViewer changes={entryChanges} referenceNames={referenceNames} />
                </div>
              )}

            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Fields to exclude from formatted document state display.
 */
const INTERNAL_FIELDS = new Set([
  'id', 'company_id', 'tenant_id', 'is_deleted', 'created_by',
  'updated_by', '_id', 'schema_version', 'photo_s3_key'
]);

/**
 * Format a field name for display (convert snake_case to Title Case).
 */
const formatSnapshotFieldName = (fieldName) => {
  if (!fieldName) return '';
  return fieldName
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
    .trim();
};

/**
 * Format a snapshot value for display.
 */
const formatSnapshotValue = (value, referenceNames) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    return capAuditText(value.join(', '));
  }
  if (typeof value === 'object') return capAuditText(JSON.stringify(value));
  if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/)) {
    const formatted = formatTimestamp(value);
    return formatted === 'Unknown' ? value : formatted;
  }
  const label = labelForId(value, referenceNames);
  if (label) return label;
  return capAuditText(String(value));
};

/**
 * Snapshot Modal Component — shows changes and formatted document state.
 */
const SnapshotModal = ({ snapshot, entry, onClose, referenceNames }) => {
  const [showFullState, setShowFullState] = useState(false);

  if (!snapshot) return null;

  const changes = deriveChanges(entry);
  const hasChanges = Boolean(changes && Object.keys(changes).length > 0);
  const statusChange = entry?.details?.status_change;
  const isCreate = snapshot.action === 'create';

  // Filter snapshot fields for display
  const snapshotEntries = snapshot.snapshot
    ? Object.entries(snapshot.snapshot).filter(
        ([key, value]) =>
          !INTERNAL_FIELDS.has(key) &&
          value !== null &&
          value !== undefined &&
          value !== ''
      )
    : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-900">
            Document State at {formatTimestamp(snapshot.timestamp)}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XCircle className="w-6 h-6" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto max-h-[60vh]">
          <div className="text-sm text-gray-600 mb-4">
            <span className="font-medium">Action:</span> {getActionStyle(snapshot.action).label}
            <span className="mx-2">|</span>
            <span className="font-medium">By:</span> {snapshot.actor?.email}
          </div>

          {/* Status Change */}
          {statusChange && (
            <div className="mb-4">
              <StatusChangeDiff
                oldStatus={statusChange.old_status || statusChange.old}
                newStatus={statusChange.new_status || statusChange.new}
                reason={statusChange.reason}
              />
            </div>
          )}

          {/* Changes (for update actions) */}
          {hasChanges && !isCreate && (
            <div className="mb-4">
              <h5 className="text-sm font-semibold text-gray-700 mb-2">Changes Made</h5>
              <DiffViewer changes={changes} referenceNames={referenceNames} />
            </div>
          )}

          {/* No changes info */}
          {!hasChanges && !isCreate && !statusChange && (
            <div className="mb-4 text-sm text-gray-500 italic">
              No field-level changes recorded for this entry.
            </div>
          )}

          {/* Full document state — shown by default for create, toggleable for updates */}
          {isCreate ? (
            <div>
              <h5 className="text-sm font-semibold text-gray-700 mb-2">Document Created With</h5>
              <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                  <tbody>
                    {snapshotEntries.map(([key, value]) => (
                      <tr key={key} className="border-b border-gray-100 last:border-b-0">
                        <td className="px-3 py-2 font-medium text-gray-700 w-1/3 align-top bg-gray-100">
                          {formatSnapshotFieldName(key)}
                        </td>
                        <td className="px-3 py-2 text-gray-800 break-words">
                          {formatSnapshotValue(value, referenceNames)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            snapshotEntries.length > 0 && (
              <div>
                <button
                  onClick={() => setShowFullState(!showFullState)}
                  className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 font-medium mb-2"
                >
                  {showFullState ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  {showFullState ? 'Hide' : 'Show'} Full Document State
                </button>
                {showFullState && (
                  <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                    <table className="w-full text-sm">
                      <tbody>
                        {snapshotEntries.map(([key, value]) => (
                          <tr key={key} className="border-b border-gray-100 last:border-b-0">
                            <td className="px-3 py-2 font-medium text-gray-700 w-1/3 align-top bg-gray-100">
                              {formatSnapshotFieldName(key)}
                            </td>
                            <td className="px-3 py-2 text-gray-800 break-words">
                              {formatSnapshotValue(value, referenceNames)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )
          )}
        </div>

        <div className="p-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * AuditTrailViewer Component
 *
 * Main component for displaying entity audit history in a timeline format.
 *
 * @param {Object} props
 * @param {string} props.entityType - Type of entity (hazard, risk_assessment, etc.)
 * @param {string} props.entityId - ID of the entity
 * @param {string} props.title - Optional custom title
 */
const AuditTrailViewer = ({ entityType, entityId, title = 'Audit History' }) => {
  const {
    history,
    loading,
    error,
    totalEntries,
    hasMore,
    loadMore,
    refresh,
    referenceNames
  } = useAuditHistory(entityType, entityId, { limit: 20, includeVersions: entityType === 'risk_assessment' });

  const {
    snapshot,
    loading: snapshotLoading,
    fetchSnapshot,
    clearSnapshot
  } = useAuditSnapshot(entityType, entityId);

  const [selectedEntry, setSelectedEntry] = useState(null);

  const handleViewSnapshot = async (auditId, entry) => {
    setSelectedEntry(entry || null);
    await fetchSnapshot(auditId);
  };

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-600">
            <AlertCircle className="w-5 h-5" />
            Error Loading Audit History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600">{error}</p>
          <button
            onClick={refresh}
            className="mt-4 flex items-center gap-2 text-blue-600 hover:text-blue-800"
          >
            <RefreshCw className="w-4 h-4" />
            Try Again
          </button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            {title}
            {totalEntries > 0 && (
              <span className="text-sm font-normal text-gray-500">
                ({totalEntries} entries)
              </span>
            )}
          </div>

          <button
            onClick={refresh}
            disabled={loading}
            className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </CardTitle>
      </CardHeader>

      <CardContent>
        {loading && history.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
            <span className="ml-2 text-gray-500">Loading audit history...</span>
          </div>
        ) : history.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>No audit history available</p>
          </div>
        ) : (
          <>
            {/* Scrollable container with max height */}
            <div className="max-h-96 overflow-y-auto pr-2">
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200" />

                {/* Timeline entries */}
                <div className="space-y-4">
                  {history.map((entry) => (
                    <TimelineEntry
                      key={entry.id}
                      entry={entry}
                      entityType={entityType}
                      entityId={entityId}
                      onViewSnapshot={handleViewSnapshot}
                      referenceNames={referenceNames}
                    />
                  ))}
                </div>
              </div>
            </div>
            {history.length > 3 && (
              <p className="text-xs text-gray-400 text-center mt-2">Scroll to see all entries</p>
            )}

            {/* Load More Button */}
            {hasMore && (
              <div className="mt-4 text-center">
                <button
                  onClick={loadMore}
                  disabled={loading}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="w-4 h-4 inline-block mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    'Load More'
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </CardContent>

      {/* Snapshot Modal */}
      {snapshot && (
        <SnapshotModal
          snapshot={snapshot}
          entry={selectedEntry}
          onClose={() => { clearSnapshot(); setSelectedEntry(null); }}
          referenceNames={referenceNames}
        />
      )}

      {/* Loading overlay for snapshot */}
      {snapshotLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white rounded-lg p-4 shadow-lg">
            <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
          </div>
        </div>
      )}
    </Card>
  );
};

export default AuditTrailViewer;
